// client/src/components/common/AppToaster.tsx (ST-00259)
//
// The app's one toast container. About 40 screens report success and failure through
// react-toastify's toast.*, but until ST-00259 no <ToastContainer> was mounted anywhere, so
// every one of those messages was silently queued and never shown (found under ST-00008).
// App.tsx mounts this once, above every shell (the auth screens, the driver PWA, the tabbed
// and the untabbed shell), so a toast fired from any page shows exactly once. Do not mount
// another ToastContainer: two containers with the default id fight over the same toasts.
//
//  - Placement (AppToaster.css): top centre on a desktop, under the tab strip and the
//    back/forward bar; across the top on a phone, under the 56 px header. Every page header
//    keeps its actions on the right and its title on the left, and a modal keeps its close
//    button top right and its Save bottom right, so the top centre covers text, not
//    controls. Nothing at the bottom: that is where the phone action bars, the picker bottom
//    sheet and the Install / Stay in the loop / Pick up where you left off prompts live.
//    Toasts stack above modals and sheets, so one fired from inside a modal is not hidden
//    behind its backdrop.
//  - Accessibility: the container is a polite live region (react-toastify). Errors and
//    warnings are role="alert", everything else role="status" (the house convention, as in
//    DocumentManager and TenantDetails). A toast never takes focus: Alt+T moves to the newest
//    one, Escape goes back. On a touch screen a toast can be swiped away.
//  - Colours come from the theme tokens only (AppToaster.css), so light and dark follow
//    [data-theme] without this component reading the theme.
//  - A page that can fire the same failure repeatedly (a list that reloads on every
//    keystroke, a filter change that reloads twice) passes a stable `toastId`, so a failing
//    endpoint shows one message however often it is asked (react-toastify ignores a toast
//    whose id is already on screen).
import { cloneElement, isValidElement, type ReactElement } from 'react';
import { Slide, ToastContainer, type ToastTransitionProps } from 'react-toastify';
import './AppToaster.css';

/** How long a toast stays when the pointer is not on it (ms). */
export const TOAST_AUTO_CLOSE_MS = 5000;

/** At most this many toasts on screen; any more wait their turn. */
export const TOAST_LIMIT = 4;

const URGENT_TYPE = /\bToastify__toast--(error|warning)\b/;

interface ToastElementProps {
  className?: string;
  role?: string;
}

/**
 * Slide, plus the role each toast type should have. react-toastify gives every toast the
 * container's single `role`; this sets role="alert" on error and warning toasts and leaves
 * the container default (role="status") on the rest. A toast that was given its own role
 * keeps it. The transition receives the toast's element, so this is the one place a role can
 * follow the type without touching the ~40 call sites.
 */
export function RoleByTypeSlide({ children, ...transition }: ToastTransitionProps) {
  let toastElement = children;
  if (isValidElement(children)) {
    const el = children as ReactElement<ToastElementProps>;
    const { className = '', role } = el.props;
    // `role` is only present while the toast is in (react-toastify drops it on the way out).
    if (role === 'status' && URGENT_TYPE.test(className)) {
      toastElement = cloneElement(el, { role: 'alert' });
    }
  }
  return <Slide {...transition}>{toastElement}</Slide>;
}

export default function AppToaster() {
  return (
    <ToastContainer
      className="lx-toaster"
      toastClassName="lx-toast"
      position="top-center"
      autoClose={TOAST_AUTO_CLOSE_MS}
      limit={TOAST_LIMIT}
      newestOnTop
      hideProgressBar
      closeOnClick={false}
      pauseOnHover
      pauseOnFocusLoss
      draggable="touch"
      role="status"
      transition={RoleByTypeSlide}
      theme="light"
      aria-label="Messages (Alt+T)"
    />
  );
}
